<template>
  <div class="pi" :style="{ width: 104 * scale + 'px', height: 96 * scale + 'px' }">
    <div class="pi-box" :style="{ transform: `scale(${scale})` }">
      <!-- Тариф: карта подписки -->
      <div v-if="isTariff" class="pi-card" :style="{ background: tier.css, color: tier.ink }">
        <div class="pi-card-glare"></div>
        <svg class="pi-card-logo" width="22" height="13" viewBox="60 170 515 300"><path :d="LOGO" fill="currentColor" fill-rule="evenodd" /></svg>
        <div class="pi-card-tier">{{ plan.toUpperCase() }}</div>
        <div class="pi-card-num"><span>{{ item.months }}</span><small>МЕС</small></div>
        <div v-if="item.kind === 'jackpot'" class="pi-card-ring"></div>
      </div>

      <!-- Скидка: купон -->
      <div v-else-if="isCoupon" class="pi-coupon-shadow">
        <div class="pi-coupon" :style="{ background: couponCss }">
          <div class="pi-coupon-cut"></div>
          <div class="pi-coupon-text">
            <span class="pi-coupon-pct">−{{ item.percent }}%</span>
            <span class="pi-coupon-sub">{{ couponSub }}</span>
            <span v-if="couponSub2" class="pi-coupon-sub2">{{ couponSub2 }}</span>
          </div>
        </div>
      </div>

      <!-- Рубли: пачка купюр -->
      <div v-else-if="item.kind === 'money'" class="pi-money">
        <div class="pi-money-back"></div>
        <div class="pi-money-front">
          <div class="pi-money-frame"></div>
          <span>{{ item.amount }} ₽</span>
        </div>
      </div>

      <!-- Монеты: горка -->
      <div v-else class="pi-coins">
        <CoinIcon
          v-for="(c, i) in coinPos"
          :key="i"
          :size="c.s"
          class="pi-coin"
          :style="{ left: c.x + 'px', top: c.y + 'px' }"
        />
        <div class="pi-coins-badge">+{{ item.amount }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import CoinIcon from './CoinIcon.vue';

const props = defineProps({
  item: { type: Object, default: () => ({}) },
  scale: { type: Number, default: 1 }
});

const LOGO = 'M68 175H395L237 460L148 305H192L232 372L318 220H95Z M108 248H170L185 275H123Z M408 178L567 463H252Z M406 272L318 420H491Z';

const TIERS = {
  Premium: { css: 'linear-gradient(135deg,#FFF1B8,#F5B301 55%,#C98300)', ink: '#0F1512', coupon: 'linear-gradient(135deg,#FEF08A,#FACC15)' },
  Medium: { css: 'linear-gradient(135deg,#B7CCFF,#4F7BEA)', ink: '#fff', coupon: 'linear-gradient(135deg,#DCE6FF,#8FB0FF)' },
  Basic: { css: 'linear-gradient(135deg,#6EE7B7,#0E9F5B)', ink: '#fff', coupon: 'linear-gradient(135deg,#D1FAE5,#6EE7B7)' }
};

const isTariff = computed(() => props.item.kind === 'tariff' || props.item.kind === 'jackpot');
const isCoupon = computed(() => props.item.kind === 'disc' || props.item.kind === 'all');
const plan = computed(() => props.item.plan || 'Basic');
const tier = computed(() => TIERS[isTariff.value ? plan.value : props.item.target] || TIERS.Premium);

const couponCss = computed(() =>
  props.item.kind === 'all' ? 'linear-gradient(135deg,#FDE68A,#FACC15 50%,#34D399)' : tier.value.coupon
);
const couponSub = computed(() =>
  props.item.kind === 'all' ? 'ВСЕ ТАРИФЫ' : String(props.item.target || '').toUpperCase()
);
const couponSub2 = computed(() =>
  (props.item.minMonths || 1) > 1 ? `ОТ ${props.item.minMonths} МЕС` : ''
);

const coinPos = computed(() => {
  const n = props.item.amount || 0;
  if (n >= 100) return [{ x: 2, y: 42, s: 34 }, { x: 30, y: 44, s: 34 }, { x: 58, y: 42, s: 34 }, { x: 15, y: 22, s: 34 }, { x: 44, y: 22, s: 34 }, { x: 29, y: 0, s: 38 }];
  if (n >= 50) return [{ x: 4, y: 36, s: 40 }, { x: 52, y: 36, s: 40 }, { x: 28, y: 30, s: 42 }, { x: 28, y: 4, s: 42 }];
  return [{ x: 4, y: 30, s: 44 }, { x: 48, y: 30, s: 44 }, { x: 25, y: 8, s: 48 }];
});
</script>

<style scoped>
.pi { position: relative; flex-shrink: 0; }
.pi-box {
  position: absolute; left: 0; top: 0; width: 104px; height: 96px;
  transform-origin: 0 0; display: flex; align-items: center; justify-content: center;
}

/* Карта тарифа */
.pi-card {
  position: relative; width: 96px; height: 64px; border-radius: 12px; transform: rotate(-6deg); overflow: hidden;
  box-shadow: 0 10px 22px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.5);
}
.pi-card-glare { position: absolute; right: -18px; top: -18px; width: 60px; height: 60px; border-radius: 50%; background: rgba(255,255,255,.18); }
.pi-card-logo { position: absolute; left: 9px; top: 8px; opacity: .85; }
.pi-card-tier { position: absolute; right: 8px; top: 7px; font-size: 8px; font-weight: 800; letter-spacing: .06em; opacity: .85; }
.pi-card-num { position: absolute; left: 9px; bottom: 6px; display: flex; align-items: baseline; gap: 3px; }
.pi-card-num span { font-size: 28px; font-weight: 800; letter-spacing: -.03em; line-height: 1; }
.pi-card-num small { font-size: 10px; font-weight: 800; }
.pi-card-ring { position: absolute; inset: 0; border-radius: 12px; box-shadow: inset 0 0 0 2px rgba(255,255,255,.7); }

/* Купон */
.pi-coupon-shadow { filter: drop-shadow(0 8px 12px rgba(0,0,0,.4)); transform: rotate(5deg); }
.pi-coupon {
  position: relative; width: 96px; height: 58px; border-radius: 10px; display: flex; align-items: center;
  -webkit-mask: radial-gradient(circle 8px at 0 50%, transparent 97%, #000) left / 51% 100% no-repeat,
                radial-gradient(circle 8px at 100% 50%, transparent 97%, #000) right / 51% 100% no-repeat;
  mask: radial-gradient(circle 8px at 0 50%, transparent 97%, #000) left / 51% 100% no-repeat,
        radial-gradient(circle 8px at 100% 50%, transparent 97%, #000) right / 51% 100% no-repeat;
}
.pi-coupon-cut { position: absolute; left: 22px; top: 8px; bottom: 8px; border-left: 2px dashed rgba(15,21,18,.25); }
.pi-coupon-text { margin-left: 28px; display: flex; flex-direction: column; }
.pi-coupon-pct { font-size: 20px; font-weight: 800; letter-spacing: -.03em; color: #0F1512; line-height: 1; }
.pi-coupon-sub { font-size: 8px; font-weight: 800; letter-spacing: .05em; color: #4B5651; margin-top: 2px; }
.pi-coupon-sub2 { font-size: 7px; font-weight: 800; letter-spacing: .05em; color: #6B756F; margin-top: 1px; }

/* Купюры */
.pi-money { position: relative; width: 96px; height: 60px; }
.pi-money-back { position: absolute; inset: 0; border-radius: 9px; background: #0A7A47; transform: rotate(9deg) translate(4px, -4px); }
.pi-money-front {
  position: absolute; inset: 0; border-radius: 9px; background: linear-gradient(135deg, #34D399, #0E9F5B);
  transform: rotate(-4deg); box-shadow: 0 10px 22px rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center;
}
.pi-money-frame { position: absolute; inset: 5px; border: 1.5px solid rgba(255,255,255,.35); border-radius: 6px; }
.pi-money-front span { position: relative; font-size: 22px; font-weight: 800; color: #fff; letter-spacing: -.02em; }

/* Монеты */
.pi-coins { position: relative; width: 100px; height: 80px; }
.pi-coin { position: absolute; filter: drop-shadow(0 3px 5px rgba(0,0,0,.4)); }
.pi-coins-badge {
  position: absolute; right: -2px; bottom: -2px; background: #0F1512; border: 1.5px solid #FACC15; color: #FACC15;
  font-size: 11px; font-weight: 800; padding: 2px 7px; border-radius: 7px; transform: rotate(-4deg);
}
</style>
